/**
 * Agent Server API Client
 * For communicating with the agent-server (chat, RAG, health analysis)
 */

import { getAgentHeaders } from './settings';

const AGENT_API_BASE_URL = '/api/v1';

// Types

/** File attachment — document_id 단일 모드.
 *
 * base64 인라인 입력(media_type/data)은 서버가 명시 reject. 클라이언트는
 * POST /documents로 사전 업로드 후 발급받은 document_id로 첨부한다.
 */
export interface FileAttachment {
  document_id: string;
  name?: string;
}

export interface ChatRequest {
  thread_id: string;
  message: string;
  files?: FileAttachment[];
  prompt_label?: string;
  action?: 'send' | 'edit_resend';
  edit_target_message_id?: string;
}

export interface ChatResponse {
  thread_id: string;
  content: string;
  tool_calls?: ToolCall[];
  usage?: {
    input_tokens: number;
    output_tokens: number;
  };
}

export interface ToolCall {
  name: string;
  arguments: string;
  result?: string;
}

export interface HealthAnalysisRequest {
  data: Record<string, unknown>;
  analysis_type?: string;
}

export interface HealthAnalysisRecommendation {
  category: string;
  action: string;
  reason: string;
}

export interface HealthAnalysisDetails {
  summary?: string[];
  assessment?: string;
  positive_aspects?: string[];
  areas_of_concern?: string[];
  recommendations?: HealthAnalysisRecommendation[];
}

export interface HealthAnalysisResult {
  flag: string;
  title: string;
  analysis: HealthAnalysisDetails;
  suggested_questions?: string[];
  medical_attention_needed?: boolean;
  medical_attention_reason?: string;
  disclaimer?: string;
}

export interface HealthAnalysisContext {
  request?: Record<string, unknown>;
  user_profile?: Record<string, unknown>;
  memories?: Array<{ content: string; tags: string[] }>;
}

export interface HealthAnalysisResponse {
  analysis_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  context?: HealthAnalysisContext | Record<string, unknown>;
  result?: HealthAnalysisResult;
  error?: { code: string; detail: string };
  created_at: string;
  completed_at?: string;
}

export interface HealthAnalysisListItem {
  analysis_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  result?: { flag?: string; title?: string };
}

// SSE Event types matching agent-server SSEEventType (01-api-reference.md)
export type SSEEventType = 'thought' | 'tool_call' | 'tool_result' | 'answer' | 'meta' | 'error' | 'done';

export interface SSEEvent {
  event: SSEEventType;
  data: string;
}

/** One captured SSE line bundle — debug log, bug report attachment.
 * `rawLine` retains the exact wire format (`id: N\nevent: x\ndata: {...}`)
 * so engineers can paste into `tests/fixtures/legacy-sse-stream.ts` to reproduce.
 */
export interface SSELogEntry {
  id: number;
  timestamp: number;
  event: SSEEventType | 'heartbeat';
  rawLine: string;
  parsed?: Record<string, unknown>;
}

/** Parsed tool call info for UI display.
 *
 * Lifecycle (per docs/design/01-api-reference.md §5 tool_call 2-phase 계약):
 *   'generating' → 'calling' → 'completed'
 *
 * `generating` phase is synthesized from SSE `tool_call` with `status: 'generating'`
 * (no `input` yet). `calling` phase is the existing completion SSE event where
 * `input` arrives. `completed` fires on matching `tool_result`.
 *
 * `messageId` binds each tool call to its owning AI message so multi-turn UI can
 * filter panel entries by message.
 */
export interface ToolCallInfo {
  id: string;
  messageId: string;
  name: string;
  arguments: Record<string, unknown>;
  status: 'generating' | 'calling' | 'completed';
  result?: string;
  startTime: number;
  endTime?: number;
}

/** Known pre-stream HTTP error body shape. Server may include extras not
 * listed here, so the index signature keeps it extensible without `any`. */
export interface ChatHttpErrorExtra {
  limit_type?: string;
  limit?: number;
  current?: number;
  [key: string]: unknown;
}

/** Thrown when `POST /chats` fails before the SSE stream starts (4xx/5xx).
 *
 * The stream path (once opened) delivers errors via SSE `error` events — those
 * are handled by the hook's in-stream dispatcher. Pre-stream failures like
 * 409 GENERATION_IN_PROGRESS, 429 QUOTA_EXCEEDED (with Retry-After), 403
 * DOCUMENT_OWNERSHIP_MISMATCH, 422 validation errors reach the client as HTTP
 * responses only — this class wraps the parsed body so the UI dispatcher can
 * handle both paths uniformly.
 */
export class ChatHttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly errorCode: string,
    public readonly detail: string,
    public readonly extra: ChatHttpErrorExtra,
    public readonly retryAfter?: number,
  ) {
    super(`[${errorCode}] ${detail}`);
    this.name = 'ChatHttpError';
  }
}

/**
 * Agent Server API
 */
export const agentApi = {
  /**
   * Chat with SSE streaming.
   *
   * Returns an async generator that yields SSE events. Two failure modes:
   * 1. **Pre-stream HTTP error** (4xx/5xx before stream opens) → throws
   *    {@link ChatHttpError} with parsed body (errorCode, detail, extras like
   *    Retry-After). Hook's pre-stream try/catch dispatches to UI.
   * 2. **In-stream `error` SSE event** (LLM/tool failure mid-stream) → yielded
   *    as a normal event. Hook's `case 'error'` handler dispatches.
   *
   * @param request   Chat request body.
   * @param signal    Optional AbortSignal — call `controller.abort()` to cancel
   *                  the in-flight stream. Triggers `GeneratorExit` on the
   *                  server side, which the backend handles as user cancellation.
   * @param onRawLine Optional callback invoked for each raw SSE bundle (id +
   *                  event + data + heartbeat comments) so the UI can populate
   *                  the SSE log panel. Bundles are flushed on blank-line
   *                  boundary, matching the SSE wire protocol.
   */
  async *chatStream(
    request: ChatRequest,
    signal?: AbortSignal,
    onRawLine?: (entry: SSELogEntry) => void,
  ): AsyncGenerator<SSEEvent> {
    const response = await fetch(`${AGENT_API_BASE_URL}/chats`, {
      method: 'POST',
      headers: await getAgentHeaders({
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      }),
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      // Parse RFC 9457 error body (matches agent-server ErrorEventData shape).
      // Fall back to raw text on parse failure so the UI gets *something* useful.
      let errorCode = 'HTTP_ERROR';
      let detail = response.statusText || `HTTP ${response.status}`;
      const extra: ChatHttpErrorExtra = {};
      try {
        const body = (await response.json()) as Record<string, unknown>;
        errorCode = (body.error_code as string | undefined) ?? errorCode;
        detail = (body.detail as string | undefined) ?? detail;
        // Pull through any extension fields without leaking known ones twice.
        for (const [k, v] of Object.entries(body)) {
          if (!['error_code', 'detail', 'title', 'type', 'status', 'instance'].includes(k)) {
            extra[k] = v;
          }
        }
      } catch {
        try {
          detail = (await response.text()) || detail;
        } catch {
          // Body already consumed or unreadable — keep statusText fallback.
        }
      }
      const retryAfterHeader = response.headers.get('Retry-After');
      const retryAfter = retryAfterHeader ? Number(retryAfterHeader) : undefined;
      throw new ChatHttpError(response.status, errorCode, detail, extra, Number.isFinite(retryAfter) ? retryAfter : undefined);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventType: SSEEventType = 'answer';
    let currentEventId: number | null = null;
    let currentRawLines: string[] = [];
    let logIdCounter = 0;

    const flushRawBundle = (eventTypeForLog: SSEEventType | 'heartbeat', parsed?: Record<string, unknown>) => {
      if (!onRawLine || currentRawLines.length === 0) {
        currentRawLines = [];
        return;
      }
      const rawLine = currentRawLines.join('\n');
      onRawLine({
        id: currentEventId ?? ++logIdCounter,
        timestamp: Date.now(),
        event: eventTypeForLog,
        rawLine,
        parsed,
      });
      currentRawLines = [];
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let streamDone = false;
        for (const line of lines) {
          // Heartbeat comments (`: heartbeat`) — log but don't yield as an event.
          if (line.startsWith(':')) {
            currentRawLines.push(line);
            flushRawBundle('heartbeat');
            continue;
          }
          // Blank line marks bundle boundary (SSE protocol). The actual yield
          // happens on `data:` lines below; this is just a no-op separator.
          if (line === '') {
            continue;
          }
          if (line.startsWith('id:')) {
            currentEventId = Number(line.slice(3).trim()) || null;
            currentRawLines.push(line);
            continue;
          }
          if (line.startsWith('event:')) {
            currentEventType = line.slice(6).trim() as SSEEventType;
            currentRawLines.push(line);
            continue;
          }
          if (line.startsWith('data:')) {
            const data = line.slice(5).trim();
            if (data) {
              currentRawLines.push(line);
              let parsed: Record<string, unknown> | undefined;
              try {
                parsed = JSON.parse(data) as Record<string, unknown>;
              } catch {
                parsed = undefined;
              }
              flushRawBundle(currentEventType, parsed);
              yield { event: currentEventType, data };
              if (currentEventType === 'done') {
                streamDone = true;
              }
              // Reset for next bundle — id resets per-event by spec.
              currentEventType = 'answer';
              currentEventId = null;
            }
          }
        }
        if (streamDone) break;
      }
    } finally {
      reader.releaseLock();
    }
  },

  /**
   * Chat without streaming - always uses streaming in the backend
   * Collects all events and returns final response
   */
  async chat(request: ChatRequest): Promise<ChatResponse> {
    let content = '';
    const toolCalls: ToolCall[] = [];

    for await (const event of this.chatStream(request)) {
      if (event.event === 'answer') {
        try {
          const data = JSON.parse(event.data);
          content += data.content || '';
        } catch {
          content += event.data;
        }
      } else if (event.event === 'tool_call') {
        try {
          const data = JSON.parse(event.data);
          toolCalls.push({
            name: data.name,
            arguments: JSON.stringify(data.input || {}),
          });
        } catch {
          // Ignore parse errors
        }
      } else if (event.event === 'tool_result') {
        try {
          const data = JSON.parse(event.data);
          const lastTool = toolCalls[toolCalls.length - 1];
          if (lastTool) {
            lastTool.result = data.output || '';
          }
        } catch {
          // Ignore parse errors
        }
      }
    }

    return {
      thread_id: request.thread_id,
      content,
      tool_calls: toolCalls,
    };
  },

  /**
   * Create health analysis
   * @param userId - User ID
   * @param context - Health context matching HealthContext schema
   */
  async createHealthAnalysis(userId: string, context: Record<string, unknown>): Promise<HealthAnalysisResponse> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/health-analyses`, {
      method: 'POST',
      headers: await getAgentHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ context }),
    });

    if (!response.ok) {
      throw new Error(`Health analysis creation failed: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Get health analysis by ID (user-scoped)
   */
  async getHealthAnalysis(userId: string, analysisId: string): Promise<HealthAnalysisResponse> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/health-analyses/${analysisId}`, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get health analysis: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Poll health analysis until completed (user-scoped)
   */
  async pollHealthAnalysis(
    userId: string,
    analysisId: string,
    intervalMs: number = 1000,
    maxAttempts: number = 30,
  ): Promise<HealthAnalysisResponse> {
    for (let i = 0; i < maxAttempts; i++) {
      const analysis = await this.getHealthAnalysis(userId, analysisId);

      if (analysis.status === 'completed' || analysis.status === 'failed') {
        return analysis;
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    throw new Error('Health analysis polling timed out');
  },

  /**
   * Upload a document for RAG processing.
   *
   * 서버 응답은 `BatchDocumentUploadResponse` — 단일 file을 보내도 `results[]` 형태로
   * 반환된다. 호출자는 `results[0]`을 사용하거나 `errors`로 실패 처리.
   */
  async uploadDocument(userId: string, file: File): Promise<BatchDocumentUploadResponse> {
    const formData = new FormData();
    formData.append('files', file);

    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/documents`, {
      method: 'POST',
      headers: await getAgentHeaders(),
      body: formData,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Document upload failed: ${error}`);
    }

    return response.json();
  },

  /**
   * Delete a document (user-scoped)
   */
  async deleteDocument(userId: string, documentId: string): Promise<void> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/documents/${documentId}`, {
      method: 'DELETE',
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete document: ${response.statusText}`);
    }
  },

  /**
   * Delete a memory (user-scoped)
   */
  async deleteMemory(userId: string, memoryId: string): Promise<void> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/memories/${memoryId}`, {
      method: 'DELETE',
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete memory: ${response.statusText}`);
    }
  },

  /**
   * Delete a health analysis (user-scoped)
   */
  async deleteHealthAnalysis(userId: string, analysisId: string): Promise<void> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/health-analyses/${analysisId}`, {
      method: 'DELETE',
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete health analysis: ${response.statusText}`);
    }
  },

  /**
   * Update a memory (user-scoped)
   */
  async updateMemory(userId: string, memoryId: string, content: string): Promise<AgentMemory> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/memories/${memoryId}`, {
      method: 'PATCH',
      headers: await getAgentHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ content }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update memory: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * List memory history for a user
   */
  async listMemoryHistory(
    userId: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<MemoryHistoryResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());

    const url = `${AGENT_API_BASE_URL}/users/${userId}/memories/history${
      queryParams.toString() ? '?' + queryParams.toString() : ''
    }`;
    const response = await fetch(url, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to list memory history: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Get available tools (MCP + built-in)
   */
  async getTools(): Promise<ToolsResponse> {
    const response = await fetch(`${AGENT_API_BASE_URL}/tools`, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get tools: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * List documents for a user (agent-server direct)
   */
  async listDocuments(
    userId: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<AgentDocumentListResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());

    const url = `${AGENT_API_BASE_URL}/users/${userId}/documents${
      queryParams.toString() ? '?' + queryParams.toString() : ''
    }`;
    const response = await fetch(url, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to list documents: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * List memories for a user (agent-server direct)
   */
  async listMemories(userId: string): Promise<AgentMemoryListResponse> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/memories`, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to list memories: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * List health analyses for a user
   */
  async listHealthAnalyses(
    userId: string,
    params: { status?: string; limit?: number; offset?: number } = {},
  ): Promise<HealthAnalysisListResponse> {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());

    const url = `${AGENT_API_BASE_URL}/users/${userId}/health-analyses${
      queryParams.toString() ? '?' + queryParams.toString() : ''
    }`;

    const response = await fetch(url, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to list health analyses: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Get document detail with chunks and summary
   */
  async getDocument(userId: string, documentId: string): Promise<DocumentDetail> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/documents/${documentId}`, {
      headers: await getAgentHeaders(),
    });
    if (!response.ok) throw new Error(`Failed to get document: ${response.status}`);
    return response.json();
  },

  /**
   * Update document (e.g. toggle is_active)
   */
  async updateDocument(userId: string, documentId: string, data: { is_active?: boolean }): Promise<AgentDocument> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/documents/${documentId}`, {
      method: 'PATCH',
      headers: await getAgentHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`Failed to update document: ${response.status}`);
    return response.json();
  },

  async getServerVersion(): Promise<string> {
    const response = await fetch(`${AGENT_API_BASE_URL}/health`, {
      headers: await getAgentHeaders(),
    });
    if (!response.ok) return '0.0.0';
    const data = await response.json();
    return data.version ?? '0.0.0';
  },
};

// Document upload response types (BatchDocumentUploadResponse — agent-server schema)
export interface DocumentUploadFileInfo {
  name: string;
  size: number;
  type: string;
}

export interface DocumentUploadResult {
  document_id: string;
  status: string; // 'processing' 등
  file: DocumentUploadFileInfo;
}

export interface DocumentUploadError {
  file_name: string;
  error_code: string; // FileValidationCode: FILE_TOO_LARGE_FOR_TIER | FILE_TOO_LARGE_FOR_MODEL | UNSUPPORTED_MIME_TYPE | TOO_MANY_FILES_PER_REQUEST | REQUEST_PAYLOAD_TOO_LARGE | CONTENT_TYPE_MISMATCH | DUPLICATE_FILE | UPLOAD_FAILED
  detail: string;
}

export interface DocumentUploadWarning {
  file_name?: string;
  warning_code?: string;
  detail?: string;
}

export interface BatchDocumentUploadResponse {
  results: DocumentUploadResult[];
  errors: DocumentUploadError[];
  warnings: DocumentUploadWarning[];
}

// Tool info type
export interface ToolInfo {
  id: string;
  name: string;
  description: string;
  type: 'built-in' | 'mcp';
}

export interface ToolsResponse {
  tools: ToolInfo[];
}

// Health analysis list response
export interface HealthAnalysisListResponse {
  analyses: HealthAnalysisListItem[];
  total: number;
}

// Agent-server document type (nested file info)
export interface AgentDocument {
  id: string;
  file: {
    name: string;
    size: number;
    type: string;
  };
  summary: string | null;
  status: string;
  source: string;
  is_active: boolean;
  created_at: string;
}

// Document detail response (GET /users/{user_id}/documents/{document_id})
export interface DocumentChunkItem {
  index: number;
  content: string;
  metadata: Record<string, unknown>;
}

export interface DocumentStats {
  search_count: number;
  last_searched_at: string | null;
}

export interface DocumentDetail {
  id: string;
  file: {
    name: string;
    size: number;
    type: string;
  };
  summary: string | null;
  chunks: DocumentChunkItem[];
  total_chunks: number;
  stats: DocumentStats;
  status: string;
  source: string;
  created_at: string;
}

export interface AgentDocumentListResponse {
  documents: AgentDocument[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

// Agent-server memory type
export interface AgentMemory {
  memory_id: string;
  content: string;
  category: string | null;
  tags: string[];
  created_at: string;
}

export interface AgentMemoryListResponse {
  memories: AgentMemory[];
}

// Memory history types
export interface MemoryHistoryItem {
  id: string;
  memory_id: string;
  event: 'ADD' | 'UPDATE' | 'DELETE';
  old_content: string | null;
  new_content: string | null;
  source: string;
  extra_data: Record<string, unknown>;
  created_at: string;
}

export interface MemoryHistoryResponse {
  history: MemoryHistoryItem[];
  pagination: { total: number; limit: number; offset: number; has_more: boolean };
}

export default agentApi;
